import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { clsx } from "clsx";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState } from "@/features/power_projects/components/ui";
import {
  useCancelMessages,
  useQueueMessages,
  useQueueState,
  useReorderQueue,
  type QueuedMessage,
} from "@/features/leads/api/campaigns";

/**
 * Kolejka wysyłki — wszystkie kampanie na jednej liście, w kolejności wychodzenia.
 *
 * Jedyne miejsce, w którym widać, KTÓRA firma dostanie maila jako następna: ekran
 * kampanii pokazuje tylko jej własny wycinek, a limit dzienny jest wspólny dla
 * wszystkich kampanii. Kolejność bierze się stąd, skąd bierze ją dyspozytor
 * (apps/leads/campaigns.py: QUEUE_ORDER), więc pierwszy wiersz to naprawdę
 * pierwszy mail.
 *
 * Przeciąganie ustawia kolejność ręcznie. Przypomnienia zostają z przodu nawet po
 * przeciągnięciu ich w dół — firma w trakcie rozmowy jest warta więcej niż kolejna
 * firma z listy — więc po zapisie lista wraca z serwera w jego, wiążącej wersji.
 *
 * Krzyżyk przy wierszu wyjmuje mail z kolejki na stałe (zgłoszenie z 22.09.2026:
 * nie było jak wyjąć pojedynczego zatwierdzonego maila). Całą kampanię wraz z jej
 * przypomnieniami kończy się przyciskiem „Zakończ kampanię" na jej ekranie.
 */

const STEP_LABEL = (step: number) => (step === 0 ? "zapytanie" : `przypomnienie ${step}`);

function Row({
  message,
  position,
  isLastToday,
  onRemove,
  removing,
}: {
  message: QueuedMessage;
  position: number;
  isLastToday: boolean;
  onRemove: () => void;
  removing: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: message.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={clsx(
        "flex items-center gap-3 border-b border-[var(--color-border)] px-3 py-2.5",
        "bg-[var(--color-bg-elevated)]",
        isDragging && "opacity-60",
        // Linia limitu dziennego: wszystko poniżej wyjdzie dopiero kolejnego dnia.
        isLastToday && "border-b-2 border-b-[var(--color-primary)]",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Przeciągnij: ${message.lead_name}`}
        className="cursor-grab touch-none text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        <GripVertical size={14} />
      </button>
      <span className="w-6 shrink-0 text-right font-mono text-xs text-[var(--color-text-muted)]">
        {position}
      </span>
      <div className="min-w-0 flex-1">
        <Link
          to={`/leads/${message.lead}`}
          className="block truncate text-sm hover:text-[var(--color-primary)]"
        >
          {message.lead_name}
        </Link>
        <span className="block truncate font-mono text-[11px] text-[var(--color-text-muted)]">
          {message.email}
        </span>
      </div>
      <Link
        to={`/leads/campaigns/${message.campaign}`}
        className="hidden w-48 truncate text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] md:block"
      >
        {message.campaign_name}
      </Link>
      <span
        className={clsx(
          "w-32 shrink-0 font-mono text-[11px]",
          message.step > 0
            ? "text-[var(--color-primary)]"
            : "text-[var(--color-text-muted)]",
        )}
      >
        {STEP_LABEL(message.step)}
      </span>
      {/* Przewidywany moment wysyłki, a nie termin: terminy przypomnień biorą się z
          godziny poprzedniej wysyłki, więc siedzą po kilka w tej samej minucie i
          ekran wyglądał, jakby maile wychodziły paczką (zgłoszenie z 28.08.2026).
          Termin zostaje pod spodem tam, gdzie jeszcze nie nadszedł — to on mówi,
          dlaczego wiersz czeka. */}
      <span className="w-36 shrink-0 text-right font-mono text-[11px] text-[var(--color-text-muted)]">
        {message.projected_send_at
          ? format(new Date(message.projected_send_at), "dd.MM.yyyy HH:mm")
          : "—"}
        {!message.is_due && message.scheduled_at && (
          <span className="block text-[10px] opacity-70">
            termin {format(new Date(message.scheduled_at), "dd.MM.yyyy HH:mm")}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        aria-label={`Wyjmij z kolejki: ${message.lead_name}`}
        title="Wyjmij z kolejki — ten mail nie wyjdzie ani teraz, ani później"
        className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] disabled:opacity-50"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function LeadsQueuePage() {
  const { data: queue, isLoading } = useQueueMessages();
  const { data: state } = useQueueState();
  const reorder = useReorderQueue();
  const cancel = useCancelMessages();

  // Lokalna kopia na czas przeciągania: wiersz ma iść za kursorem od razu, a nie
  // dopiero po odpowiedzi serwera. Odświeżenie z serwera nadpisuje ją — to serwer
  // ma ostatnie słowo w kolejności.
  const [rows, setRows] = useState<QueuedMessage[]>([]);
  useEffect(() => {
    if (queue && !reorder.isPending) setRows(queue);
  }, [queue, reorder.isPending]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((row) => row.id === active.id);
    const to = rows.findIndex((row) => row.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(rows, from, to);
    setRows(next);
    reorder.mutate(next.map((row) => row.id));
  };

  // Wyjęcie z kolejki to anulowanie WIADOMOŚCI — endpoint kampanii, bo mail należy
  // do kampanii. Wiersz znika od razu; serwer i tak odświeży listę po sobie.
  const remove = (row: QueuedMessage) => {
    if (
      !confirm(
        `Wyjąć z kolejki ${STEP_LABEL(row.step)} do „${row.lead_name}”?\n\n` +
          "Ten mail nie wyjdzie ani teraz, ani później. Kolejnych przypomnień dla tej " +
          "firmy też nie będzie — powstają dopiero po wysłanym mailu.",
      )
    )
      return;
    cancel.mutate(
      { id: row.campaign, messageIds: [row.id] },
      { onSuccess: () => setRows((prev) => prev.filter((r) => r.id !== row.id)) },
    );
  };

  // Ile z tej listy realnie wyjdzie DZIŚ. Ogranicza to nie sam limit dobowy, tylko
  // liczba momentów wysyłki, które zostały do 18:00 — o 16:00 zostają dwa, choćby
  // limit był nietknięty. To jest jedyny powód, dla którego kolejność ma znaczenie.
  const capacity = Math.min(
    state?.slots_left_today ?? 0,
    state?.remaining_today ?? 0,
  );
  // Czekające na swój termin nie startują do dzisiejszych momentów wysyłki, więc
  // nie wliczają się do „dziś zmieści się".
  const dueRows = rows.filter((row) => row.is_due);
  const waitingCount = rows.length - dueRows.length;
  const todayCount = Math.min(capacity, dueRows.length);

  return (
    <>
      <div className="card mb-5 flex flex-wrap items-center gap-x-6 gap-y-2 p-3">
        <span className="section-label">
          W kolejce <strong className="font-mono">{rows.length}</strong>
          {waitingCount > 0 && (
            <>
              {" "}
              (w tym <strong className="font-mono">{waitingCount}</strong> czeka na
              swój termin)
            </>
          )}
        </span>
        <span className="section-label">
          Dziś zmieści się{" "}
          <strong className="font-mono">{todayCount}</strong>
          {state
            ? ` (zostało ${state.slots_left_today} momentów wysyłki, limit ${state.daily_cap}/dobę, dziś wysłano ${state.sent_since_midnight})`
            : ""}
        </span>
        {state && !state.sending_enabled && (
          <span className="font-mono text-xs font-bold text-[var(--color-primary)]">
            Wysyłka wyłączona — kolejka stoi
          </span>
        )}
        {state && state.sending_enabled && !state.within_window && (
          <span className="section-label">Poza oknem wysyłki ({state.window})</span>
        )}
        {state?.next_send_at && (
          <span className="section-label">
            Następny mail{" "}
            <strong className="font-mono">
              {format(new Date(state.next_send_at), "HH:mm")}
            </strong>
          </span>
        )}
        {reorder.isPending && <span className="section-label">Zapisuję kolejność…</span>}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyState>
            Kolejka jest pusta — nie ma zatwierdzonych maili czekających na wysyłkę.
          </EmptyState>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="flex items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-1.5">
            <span className="w-[14px] shrink-0" />
            <span className="w-6 shrink-0" />
            <span className="section-label min-w-0 flex-1">Firma</span>
            <span className="section-label hidden w-48 md:block">Kampania</span>
            <span className="section-label w-32 shrink-0">Krok</span>
            <span className="section-label w-36 shrink-0 text-right">
              Przewidywana wysyłka
            </span>
            <span className="w-[14px] shrink-0" />
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={rows.map((row) => row.id)}
              strategy={verticalListSortingStrategy}
            >
              {rows.map((row, index) => (
                <Fragment key={row.id}>
                  {!row.is_due && (index === 0 || rows[index - 1].is_due) && (
                    <p className="section-label border-t border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-1.5">
                      Poniżej: czekają na swój termin — nie zajmują dzisiejszych
                      momentów wysyłki
                    </p>
                  )}
                  <Row
                    message={row}
                    position={index + 1}
                    isLastToday={
                      index + 1 === todayCount && dueRows.length > todayCount
                    }
                    onRemove={() => remove(row)}
                    removing={cancel.isPending}
                  />
                </Fragment>
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <p className="section-label mt-3">
        Przeciągnij wiersz, żeby zdecydować, która firma dostanie maila wcześniej.
        Wśród wiadomości gotowych do wysłania przypomnienia idą przed nowymi
        zapytaniami niezależnie od ustawienia; wiadomość z terminem w przyszłości
        czeka pod kreską i niczego nie blokuje. Maile wychodzą pojedynczo, w
        momentach losowanych na starcie dnia w oknie{" "}
        {state?.window ?? "06:00–18:00"} — nigdy całą paczką naraz. Godzina przy
        wierszu to przewidywany moment wysyłki: dziś z wylosowanego planu dnia, w
        kolejnych dniach szacunkowo (plan na dany dzień powstaje dopiero tego dnia).
        Drobniejszy „termin" pod spodem to data, od której wolno wysłać. Krzyżyk
        przy wierszu wyjmuje mail z kolejki na stałe; całą kampanię razem z jej
        przypomnieniami kończy przycisk „Zakończ kampanię" na ekranie kampanii.
      </p>
    </>
  );
}
